class YevoriConversion {
  constructor() {
    this.bindQuickAdd();
    this.bindProductAdd();
  }

  cartEndpoint(path) {
    const endpoint = new URL((window.Shopify?.routes?.root || '/') + path, window.location.origin);
    const countryCode = document.documentElement.dataset.countryCode;
    if (countryCode) endpoint.searchParams.set('country', countryCode);
    return endpoint.toString();
  }

  setProductAddState(form, state) {
    form.dataset.addState = state;
    const root = form.closest('[data-product-root]');
    const buttons = root
      ? root.querySelectorAll('[data-add-to-cart], [data-sticky-add]')
      : form.querySelectorAll('[data-add-to-cart]');
    const purchaseState = form.dataset.variantState || 'available';
    const pending = state === 'pending';
    const added = state === 'added';

    buttons.forEach((button) => {
      const disabled = pending || added || purchaseState !== 'available';
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      if (pending) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      const label = button.querySelector('[data-add-label]') || button;
      label.textContent = pending
        ? button.dataset.adding || 'Adding…'
        : added
          ? button.dataset.added || 'Added'
          : purchaseState === 'available'
            ? button.dataset.available || 'Add to bag'
            : purchaseState === 'sold-out'
              ? button.dataset.soldOut || 'Sold out'
              : button.dataset.unavailable || 'Unavailable';
    });
  }

  message(form, key, fallback) {
    const button = form.querySelector('[data-add-to-cart]') || form.querySelector('[data-quick-add-button]');
    return button?.dataset[key] || fallback;
  }

  showError(form, message, { checkCart = false } = {}) {
    let errorMessage = form.querySelector('[data-product-form-error], [data-quick-add-error]');
    if (!errorMessage) {
      errorMessage = document.createElement('p');
      errorMessage.className = 'product-form__error';
      errorMessage.dataset.quickAddError = '';
      errorMessage.setAttribute('role', 'alert');
      form.appendChild(errorMessage);
    }
    errorMessage.textContent = message;
    errorMessage.hidden = false;
    if (checkCart) {
      const cartLink = document.createElement('a');
      cartLink.href = this.cartEndpoint('cart');
      cartLink.textContent = this.message(form, 'viewBag', 'View bag');
      errorMessage.append(' ', cartLink);
    }
  }

  clearError(form) {
    const errorMessage = form.querySelector('[data-product-form-error], [data-quick-add-error]');
    if (!errorMessage) return;
    errorMessage.hidden = true;
    errorMessage.textContent = '';
  }

  bindProductAdd(container = document) {
    container.querySelectorAll('.product-form').forEach((form) => {
      if (form.dataset.productAddBound === 'true') return;
      form.dataset.productAddBound = 'true';

      form.addEventListener('submit', async (event) => {
        if (event.defaultPrevented) return;
        const submitter = event.submitter || form.querySelector('[data-add-to-cart]');
        if (!submitter?.matches('[data-add-to-cart]')) return;

        event.preventDefault();
        if (form.dataset.addState === 'pending' || form.dataset.addState === 'added') return;
        if (form.dataset.variantState && form.dataset.variantState !== 'available') return;
        if (!form.querySelector('[name="id"]')?.value || !form.reportValidity()) return;

        const formData = new FormData(form);
        this.clearError(form);
        this.setProductAddState(form, 'pending');
        let added = false;

        try {
          const response = await fetch(this.cartEndpoint('cart/add.js'), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });
          const payload = await response.json().catch(() => ({}));

          if (!response.ok) {
            this.showError(form, payload.description || payload.message || this.message(form, 'addError', 'This item could not be added. Please review your selection and try again.'));
            this.setProductAddState(form, 'ready');
            return;
          }

          added = true;
          this.setProductAddState(form, 'added');
          this.refreshCartCount().catch(() => {});
          document.dispatchEvent(new CustomEvent('yevori:cart-added', { detail: payload }));
          if (window.yevoriCart) {
            await window.yevoriCart.open(submitter).catch(() => window.location.assign(this.cartEndpoint('cart')));
            this.setProductAddState(form, 'ready');
          } else window.location.assign(this.cartEndpoint('cart'));
        } catch (error) {
          this.showError(
            form,
            added
              ? this.message(form, 'addedToBag', 'Added to your bag.')
              : this.message(form, 'addUnconfirmed', 'We could not confirm your bag update. Check your bag before trying again.'),
            { checkCart: true }
          );
          if (!added) this.setProductAddState(form, 'ready');
        }
      });
    });
  }

  resetQuickAdd(form) {
    const button = form.querySelector('[data-quick-add-button]');
    form.dataset.addState = 'ready';
    if (!button) return;
    button.textContent = button.dataset.originalLabel || 'Add to bag';
    button.disabled = false;
    button.setAttribute('aria-disabled', 'false');
    button.removeAttribute('aria-busy');
  }

  bindQuickAdd(container = document) {
    container.querySelectorAll('[data-quick-add-form]').forEach((form) => {
      if (form.dataset.quickAddBound === 'true') return;
      form.dataset.quickAddBound = 'true';

      form.addEventListener('submit', async (event) => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        const button = form.querySelector('[data-quick-add-button]');
        if (!button || button.disabled || form.dataset.addState === 'pending' || form.dataset.addState === 'added') return;
        const formData = new FormData(form);

        button.dataset.originalLabel = button.textContent.trim();
        form.dataset.addState = 'pending';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.adding || 'Adding…';
        this.clearError(form);

        try {
          const response = await fetch(this.cartEndpoint('cart/add.js'), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });
          const payload = await response.json().catch(() => ({}));

          if (!response.ok) {
            this.showError(form, payload.description || payload.message || this.message(form, 'addError', 'This item could not be added. Please review your selection and try again.'));
            this.resetQuickAdd(form);
            return;
          }

          form.dataset.addState = 'added';
          button.textContent = button.dataset.added || 'Added';
          button.removeAttribute('aria-busy');
          this.refreshCartCount().catch(() => {});
          document.dispatchEvent(new CustomEvent('yevori:cart-added', { detail: payload }));
          if (window.yevoriCart) {
            await window.yevoriCart.open(button).catch(() => window.location.assign(this.cartEndpoint('cart')));
          } else window.location.assign(this.cartEndpoint('cart'));
          this.resetQuickAdd(form);
        } catch (error) {
          this.showError(form, this.message(form, 'addUnconfirmed', 'We could not confirm your bag update. Check your bag before trying again.'), { checkCart: true });
          this.resetQuickAdd(form);
        }
      });
    });
  }

  async refreshCartCount() {
    const response = await fetch(this.cartEndpoint('cart.js'), {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return;
    const cart = await response.json();
    const cartLink = document.querySelector('.header-cart');
    if (!cartLink) return;

    let count = cartLink.querySelector('.cart-count');
    if (cart.item_count > 0 && !count) {
      count = document.createElement('span');
      count.className = 'cart-count';
      count.setAttribute('aria-hidden', 'true');
      cartLink.appendChild(count);
    }
    if (count) {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    }
  }
}

const initYevoriConversion = () => {
  window.yevoriConversion = new YevoriConversion();
};

document.addEventListener('DOMContentLoaded', initYevoriConversion);
document.addEventListener('shopify:section:load', initYevoriConversion);
window.addEventListener('pageshow', (event) => {
  if (!event.persisted || !window.yevoriConversion) return;
  document.querySelectorAll('.product-form[data-add-state]').forEach((form) => {
    window.yevoriConversion.setProductAddState(form, 'ready');
  });
  document.querySelectorAll('[data-quick-add-form][data-add-state]').forEach((form) => {
    window.yevoriConversion.resetQuickAdd(form);
  });
  window.yevoriConversion.refreshCartCount().catch(() => {});
});
